import { WORKSPACE_FEATURES } from './types.js';
const RANK = { guest: 0, member: 1, admin: 2, owner: 3 };
export function atLeast(role, min) {
    return RANK[role] >= RANK[min];
}
const MIN_ROLE = {
    'org:manage': 'owner',
    'org:invite': 'admin',
    'member:manage': 'admin',
    'space:create': 'admin',
    'space:edit': 'admin',
    'space:delete': 'admin',
    'space:members': 'admin',
    /**
     * Opening a board is part of doing the work, not of running the company: the
     * person who picks up a new piece of work is the one who knows a board is
     * needed for it. Guests are still shown boards rather than given them.
     */
    'project:create': 'member',
    'project:edit': 'member',
    /**
     * Renaming is split out of `project:edit` because it is the one change to a
     * project that everyone else has to relearn: the name is how the board is
     * referred to in chat and in tasks, and the key is stamped on every task
     * number ever quoted. Members still edit the description, colour, WIP limits
     * and membership of the boards they are on.
     */
    'project:rename': 'admin',
    /**
     * Archiving is how a finished board is put away, and restoring is the same
     * call in reverse — neither loses a task, so both sit with editing rather
     * than with deletion. The server also asks for real membership of the board,
     * so this is a member tidying up their own work, not anyone's.
     */
    'project:archive': 'member',
    'project:delete': 'admin',
    'task:create': 'member',
    'task:edit': 'member',
    'task:delete': 'member',
    'channel:create': 'member',
    'channel:delete': 'admin',
    'message:send': 'guest',
    'message:moderate': 'admin',
    'file:upload': 'member',
    'file:delete': 'member',
    /**
     * The shelves everyone files onto describe how the workspace organises its
     * documents, so naming them sits with administration. Anyone who can upload
     * still chooses which shelf their file goes on.
     */
    'file:categories': 'admin',
    'whiteboard:edit': 'member',
    /* A guest can be shown a sheet, but typing in one is a member's job. */
    'sheet:edit': 'member',
    'doc:edit': 'member',
    /* A guest can join a conversation about a file even where they cannot
       change the file — that is most of what being shown one is for. */
    'comment:write': 'guest',
    'meeting:create': 'member',
    'meeting:host': 'member',
    // Rules act on everyone's tasks, so keep authoring to admins and up.
    'automation:manage': 'admin',
    // The games room is the one place guests are as welcome as anyone else.
    'game:play': 'guest',
    'time:log': 'member',
    // Team management: reading reports and the audit trail, publishing
    // announcements, and reviewing leave are supervision work — admins and up.
    'report:view': 'admin',
    'audit:view': 'admin',
    'announcement:manage': 'admin',
    'checkin:manage': 'admin',
    'leave:manage': 'admin',
    'schedule:manage': 'admin',
    'onboarding:manage': 'admin',
    'survey:manage': 'admin',
    'office:manage': 'admin',
    // Departments and reporting lines describe the company, so drawing them is
    // administration — but reading the chart is not gated at all: knowing who
    // your manager is should not require permission.
    'hierarchy:manage': 'admin',
    // Authoring the documents everyone signs sits with announcements: the same
    // people who address the whole workspace also write its rules. Reading a
    // published policy, and signing it, is open to every member.
    'policy:manage': 'admin',
    /**
     * The books are the one part of running the workspace that is not shared
     * with administrators: pay, vendor terms and what a department costs are
     * the owner's to read, and an admin promoted to keep the check-in schedule
     * running has not been handed them. Reading and writing are separate
     * permissions even though both sit at `owner` today — the day expenses are
     * opened up for members to submit, only one of the two has to move.
     */
    'finance:view': 'owner',
    'finance:manage': 'owner',
    /**
     * Claiming back what you paid for is not reading the books.
     *
     * This is the half that moved: a member may submit an expense, see the ones
     * they submitted and withdraw one nobody has ruled on yet. What they may
     * never see is anybody else's, or a budget, or a total — those are still
     * `finance:view`, and every endpoint that serves them is still the owner's.
     */
    'expense:submit': 'member',
    // Keeping the supplier list is operational work, and the people who chase a
    // vendor are the people who run the office rather than the books.
    'vendor:manage': 'admin',
    /**
     * Asking to buy something is part of doing the job — the person who runs out
     * of a thing is the person who knows it is needed — so the request is a
     * member's. Agreeing to it is spending the company's money, and sits with
     * the rest of the books at owner. That split is the entire point of the
     * module: without it a purchase order is a form nobody had to sign.
     */
    'purchase:request': 'member',
    'purchase:approve': 'owner',
    // What customers owe is the other half of the books, gated with the first.
    'invoice:manage': 'owner',
    /**
     * Selling is work, not supervision: a member owns their accounts and their
     * deals. `crm:manage` is the tidying-up half — deleting an account, moving
     * somebody else's deal — which is an administrator's.
     */
    'crm:use': 'member',
    'crm:manage': 'admin',
    // Handing out laptops is IT, not finance: an administrator can run the
    // register without being shown what anything on it cost the company.
    'asset:manage': 'admin',
    /**
     * Pay is the one thing an administrator is not handed with the rest of
     * administration. Kept at owner alongside the books, and separate from
     * `hr:view` on purpose — the person keeping employment records current is
     * usually not the person who should see everyone's salary.
     */
    'payroll:view': 'owner',
    'payroll:manage': 'owner',
    // Employment records: job titles, start dates, emergency contacts. Reading
    // is not open to everyone the way the org chart is, because this is where
    // probation dates and leaving reasons live. Your own record always is.
    'hr:view': 'admin',
    'hr:manage': 'admin',
    'recruiting:manage': 'admin',
    // Opening a review cycle is administration; writing your own review, and a
    // manager writing their reports', needs no permission beyond being asked.
    'performance:manage': 'admin',
    'mail:manage': 'admin',
};
export function can(role, permission) {
    return atLeast(role, MIN_ROLE[permission]);
}
/**
 * What a member can actually open: their own product access, minus anything
 * the workspace has switched off. The single place the two layers meet, so the
 * middleware, `/auth/me` and the member list cannot drift apart.
 *
 * Owners hold every product by definition, but a workspace-wide switch still
 * applies to them — otherwise "off" would mean "off for everyone except the
 * person looking at the toggle". Administration is gated on role, not on a
 * feature, so an owner who turns everything off can still turn it back on.
 */
export function effectiveFeatures(role, assigned, disabled = []) {
    const base = role === 'owner' ? WORKSPACE_FEATURES : (assigned ?? WORKSPACE_FEATURES);
    return base.filter((feature) => !disabled.includes(feature));
}
